import React, { useEffect, useState } from "react";
import "../../../css/AddRoleModal.css";
// import { useMsal } from "@azure/msal-react";
// import { apiScopes } from "../../config/msalConfig";
// import axios from "axios";
import api from '../../../api/axiosConfig';
import { showToast } from '../../Shared/Toaster';


interface RoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (role: any) => void;
}

interface FormData {
  _id: string;
  name: string;
  category: string; // "ADMIN" | "STAFF" | "TELE"
}

const RoleModal: React.FC<RoleModalProps> = ({ isOpen, onClose, onCreate }) => {
  // const { instance, accounts } = useMsal();

  const [forms, setForms] = useState<FormData[]>([]);
  const [selectedForms, setSelectedForms] = useState<string[]>([]);
  const [roleName, setRoleName] = useState("");
  const [roleType, setRoleType] = useState("");
  const [details, setDetails] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<{ roleName?: string; roleType?: string }>({});

  // Fetch forms when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchForms();
    }
  }, [isOpen]);

  const fetchForms = async () => {
    setLoading(true);
    setError(null);
    try {
      // const response = await instance.acquireTokenSilent({
      //   ...apiScopes,
      //   account: accounts[0],
      // });
      // const token = response.accessToken;

      const token = sessionStorage.getItem("accessToken");

      const res = await api.auth_api.get<FormData[]>("/forms", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setForms(res.data);
    } catch (err: any) {
      console.error("Error fetching forms:", err);
      setError(
        err.response?.data?.message || "Could not load forms. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Checkbox handler
  const handleCheckboxChange = (formId: string) => {
    setSelectedForms((prev) =>
      prev.includes(formId)
        ? prev.filter((id) => id !== formId)
        : [...prev, formId]
    );
  };

  // ✅ Create Role API
  const handleCreateRole = async () => {
    const errors: { roleName?: string; roleType?: string } = {};

    if (!roleName.trim()) {
      errors.roleName = "Role name is required.";
    }
    if (!roleType) {
      errors.roleType = "Role type is required.";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showToast("Please fill all required fields.", 'error');
      return;
    }

    setFormErrors({});
    setLoading(true);
    try {
      // const response = await instance.acquireTokenSilent({
      //   ...apiScopes,
      //   account: accounts[0],
      // });
      // const token = response.accessToken;

      const token = sessionStorage.getItem("accessToken");

      const res = await api.auth_api.post("/roles",
        {
          name: roleName,
          key: roleName.toUpperCase(),
          type: roleType,
          details,
          formIds: selectedForms,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      showToast("Role created successfully ✅", 'success');
      setRoleName("");
      setRoleType("");
      setDetails("");
      setSelectedForms([]);

      onCreate(res.data);
      onClose();
    } catch (err: any) {
      console.error("Error creating role:", err);
      showToast(
        err.response?.data?.message || "Could not create role ❌"
      , 'error');
    } finally {
      setLoading(false);
    }
  };

  // ⭐ MODIFICATION 1: New handler for the role type dropdown
  const handleRoleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRoleType = e.target.value;
    setRoleType(newRoleType);
    setSelectedForms([]); // Clear selected forms when role type changes
  };

  if (!isOpen) return null;

  const groupedForms: Record<string, FormData[]> = {
    "QAVACH STAFF": forms.filter(
      (f) => f.category?.toUpperCase() === "QAVACH STAFF"
    ),
    "TELE SPECIALIST": forms.filter(
      (f) => f.category?.toUpperCase() === "TELE SPECIALIST"
    ),
  };

  return (
    <div className="role-modal-overlay" onClick={onClose}>
      <div className="role-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="role-modal-header">
          <h2 className="role-modal-title">Add New Role</h2>
          <button className="role-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Role Name */}
        <label className="role-modal-label">Role Name</label>
        <input
          type="text"
          className="role-modal-input"
          placeholder="Enter role name"
          value={roleName}
          onChange={(e) => {
            setRoleName(e.target.value);
            setFormErrors((prev) => ({ ...prev, roleName: undefined }));
          }}
        />
        {formErrors.roleName && (
          <p className="role-error-text">{formErrors.roleName}</p>
        )}

        {/* Role Type */}
        <label className="role-modal-label">Category</label>
        <select
          className="role-modal-input"
          value={roleType}
          onChange={(e) => {
            handleRoleTypeChange(e);
            setFormErrors((prev) => ({ ...prev, roleType: undefined }));
          }}
        >
          <option value="">-- Select Role Type --</option>
          <option value="QAVACH STAFF">QAVACH STAFF</option>
          <option value="TELE SPECIALIST">TELE SPECIALIST</option>
        </select>
        {formErrors.roleType && (
          <p className="role-error-text">{formErrors.roleType}</p>
        )}

        {/* Details */}
        <label className="role-modal-label">Details</label>
        <textarea
          className="role-modal-input"
          placeholder="Enter details"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
        />

        {/* ⭐ MODIFICATION 3: Updated JSX for conditional rendering */}
        <label className="role-modal-label">Assign Forms</label>
        <div className="role-form-groups">
          {roleType ? (
            <div>
              <h4>{roleType}</h4>
              {groupedForms[roleType] && groupedForms[roleType].length > 0 ? (
                groupedForms[roleType].map((form) => (
                  <label key={form._id}>
                    <input
                      type="checkbox"
                      checked={selectedForms.includes(form._id)}
                      onChange={() => handleCheckboxChange(form._id)}
                    />
                    {form.name}
                  </label>
                ))
              ) : (
                <p className="role-empty-text">No forms available for this role type.</p>
              )}
            </div>
          ) : (
            <p className="role-empty-text">Please select a Role Type to see forms.</p>
          )}
        </div>

        {/* Button */}
        <button className="role-create-btn" onClick={handleCreateRole}>
          {loading ? "Creating..." : "Create Role"}
        </button>
      </div>
    </div>
  );
};

export default RoleModal;